import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Alert, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { apiService } from '../services/apiService';
import PartMappingFields from './PartMappingFields';
import { emptyMapping, suggestMapping, validMapping } from '../services/partMapping';

export function validateHeaders(headers) {
  if (!headers.length || headers.length > 100) throw new Error('A BOM must have between 1 and 100 columns.');
  const names = headers.map(header => String(header ?? '').trim());
  if (names.some(name => !name || name.length > 120)) throw new Error('Column names must contain 1–120 characters.');
  if (new Set(names.map(name => name.toLowerCase())).size !== names.length) throw new Error('Column names must be unique.');
  return names;
}

export default function BOMUploadV2() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [partMapping, setPartMapping] = useState(emptyMapping);
  const [params] = useSearchParams();
  const [projectId, setProjectId] = useState(params.get('project') || '');
  const [projects, setProjects] = useState([]);
  const [projectError, setProjectError] = useState('');
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const result = await apiService.listWorkspaces();
        if (active) { setProjects(result.projects.filter(project => ['owner', 'editor'].includes(project.role))); setProjectsLoaded(true); }
      } catch (failure) { if (active) setProjectError(failure.response?.data?.error || failure.message); }
    };
    load();
    return () => { active = false; };
  }, []);
  const select = useCallback(async selected => {
    setError(''); setFile(null); setPreview(null); setPartMapping(emptyMapping()); setParsing(true);
    try {
      if (selected.size > 50 * 1024 * 1024) throw new Error('Files must not exceed 50 MB.');
      const extension = selected.name.split('.').pop().toLowerCase();
      let rows;
      if (extension === 'csv') {
        const result = Papa.parse(await selected.text(), { skipEmptyLines: 'greedy' });
        if (result.errors.length) throw new Error(result.errors[0].message);
        rows = result.data;
      } else if (extension === 'xlsx') {
        const workbook = XLSX.read(await selected.arrayBuffer(), { type: 'array' });
        rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { header: 1, defval: '', blankrows: false });
      } else throw new Error('Only CSV and XLSX files are supported.');
      const headers = validateHeaders(rows[0] || []);
      if (rows.slice(1).some(row => row.length > headers.length)) throw new Error('A row has more cells than the header.');
      setPreview({ headers, rows: rows.slice(1, 11), count: rows.length - 1 });
      setPartMapping(suggestMapping(headers.map(header => ({ id: header, name: header }))));
      setFile(selected);
      setName(selected.name.replace(/\.[^/.]+$/, '').slice(0, 160));
    } catch (failure) { setError(failure.message); }
    finally { setParsing(false); }
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: files => { if (files[0]) select(files[0]); },
    onDropRejected: () => setError('Choose one CSV or XLSX file up to 50 MB.'),
    disabled: busy || parsing, multiple: false, maxSize: 50 * 1024 * 1024,
    accept: { 'text/csv': ['.csv'], 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }
  });
  const upload = async () => {
    if (!preview || !validMapping(partMapping, preview.headers.map(header => ({ id: header })))) {
      setError('Choose your part-number columns and, if applicable, the MPN format.');
      return;
    }
    if (!name.trim() || name.trim().length > 160 || description.length > 2000) {
      setError('Enter a name of 1–160 characters and a description of at most 2,000 characters.');
      return;
    }
    setBusy(true); setError('');
    try {
      const result = await apiService.getPresignedUrl({
        filename: file.name, name: name.trim(), description: description.trim(), part_mapping: partMapping,
        ...(projectId ? { project_id: projectId } : {})
      });
      await apiService.uploadToS3(result.upload, file);
      // The editor polls authoritative processing state, including import failures.
      navigate(`/bom/${result.bom_id}`);
    } catch (failure) { setError(failure.response?.data?.error || failure.message); }
    finally { setBusy(false); }
  };
  return <div>
    <h1>BOM Upload Tool</h1><p>Upload a BOM, edit it, and share selected fields with your team and vendors.</p>
    {error && <Alert variant="danger">{error}</Alert>}
    <Card className="mb-3"><Card.Body>
      <div {...getRootProps()} className={`bomwise-dropzone p-5 text-center border rounded ${isDragActive ? 'is-drag-active' : ''}`} style={{ cursor: 'pointer' }}>
        <input {...getInputProps()} aria-label="Upload BOM file" />
        <h4>{isDragActive ? 'Drop your file here' : 'Drag a BOM here or click to browse'}</h4>
        <p className="mb-0">CSV or XLSX · up to 100 columns · up to 50 MB</p>
      </div>
      {parsing && <Spinner className="mt-3" size="sm" />}
      {file && <p className="mt-3 mb-0">{file.name} · {preview.count} rows · {preview.headers.length} columns</p>}
    </Card.Body></Card>
    {preview && <Card><Card.Body>
      <Form.Group className="mb-3" controlId="upload-project">
        <Form.Label>Destination project</Form.Label>
        <Form.Select disabled={busy} value={projectId} onChange={event => setProjectId(event.target.value)}>
          <option value="">Independent BOM (no project)</option>
          {projectId && !projects.some(project => project.project_id === projectId)
            && <option value={projectId}>Selected project — {projectsLoaded ? 'not available for upload' : 'loading…'}</option>}
          {projects.map(project => <option key={project.project_id} value={project.project_id}>{project.name}</option>)}
        </Form.Select>
        <Form.Text>Project colleagues inherit access. Project uploads belong to the project owner; vendor sharing remains per BOM.</Form.Text>
        {projectError && <Alert variant="warning" className="mt-2">{projectError} You can still upload an independent BOM.</Alert>}
      </Form.Group>
      <Form.Group className="mb-3" controlId="upload-bom-name">
        <Form.Label>BOM name</Form.Label>
        <Form.Control required maxLength={160} value={name} disabled={busy} onChange={e => setName(e.target.value)} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="upload-bom-description">
        <Form.Label>Description (optional)</Form.Label>
        <Form.Control as="textarea" rows={3} maxLength={2000} value={description} disabled={busy} onChange={e => setDescription(e.target.value)} />
      </Form.Group>
      <PartMappingFields columns={preview.headers.map(header => ({ id: header, name: header }))}
        value={partMapping} onChange={setPartMapping} disabled={busy}
        rows={preview.rows.map((row, index) => ({
          line_item_id: String(index), data: Object.fromEntries(preview.headers.map((header, i) => [header, row[i] ?? '']))
        }))} />
      <h5>File preview — first 10 rows</h5>
      <div className="table-responsive"><Table bordered size="sm">
        <thead><tr>{preview.headers.map(header => <th key={header}>{header}</th>)}</tr></thead>
        <tbody>{preview.rows.map((row, index) => <tr key={index}>{preview.headers.map((_, i) => <td key={i}>{String(row[i] ?? '')}</td>)}</tr>)}</tbody>
      </Table></div>
      <Button disabled={busy || !name.trim() || !validMapping(partMapping, preview.headers.map(header => ({ id: header }))) || (!!projectId && (!projectsLoaded || !projects.some(project => project.project_id === projectId)))} onClick={upload}>{busy && <Spinner size="sm" className="me-2" />}{busy ? 'Uploading…' : 'Upload & process BOM'}</Button>
      <p className="text-muted mt-2 mb-0">The editor supports up to 10,000 rows and 100 columns. Large responses are compressed automatically; unusually large cell contents may reach API limits. Header-only files are supported.</p>
    </Card.Body></Card>}
  </div>;
}
